"use client";

import type { FC } from "react";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { LoaderEdHero } from "../quiz/LoaderEdHero";
import { QuizProgressHeader, QuizStepLayout } from "../quiz/QuizStepLayout";
import { LOADER_COPY } from "../onboardingWizardCopy";

/** Entrance timeline — bug ubVcCwHC10Dk / Figma Loader 96121:8444 */
const CHECKLIST_ENTER_MS = 1200;
const FIRST_ROW_ACTIVE_MS = 2200;
const ROW_ADVANCE_MS = 700;

interface LoaderCheckRowProps {
  label: string;
  status: "done" | "active" | "pending";
}

const LoaderCheckRow: FC<LoaderCheckRowProps> = ({ label, status }) => (
  <div
    className={cn(
      "flex items-center justify-between gap-3 py-2.5",
      status === "pending" && "opacity-40",
    )}
  >
    <p className="body_medium_m text-content_inverse_primary">{label}</p>
    <div className="relative size-[22px] shrink-0">
      {status === "active" ? (
        <Icon name="Spinner" size={22} className="animate-spin text-white" />
      ) : status === "done" ? (
        <div className="flex size-[22px] items-center justify-center rounded-full bg-content_aura_lv3_primary">
          <Icon name="check" size={14} className="text-content_brand_primary" />
        </div>
      ) : (
        <div className="size-[22px] rounded-full border border-white/30" />
      )}
    </div>
  </div>
);

interface LoaderStepProps {
  checklist: string[];
  /** Harness / screenshot: pause checklist animation */
  freezeAutoComplete?: boolean;
}

export const LoaderStep: FC<LoaderStepProps> = ({
  checklist,
  freezeAutoComplete = false,
}) => {
  /** -1 = all pending (before first-row active beat). */
  const [activeIndex, setActiveIndex] = useState(-1);
  const [checklistVisible, setChecklistVisible] = useState(false);

  useEffect(() => {
    if (freezeAutoComplete) {
      setChecklistVisible(true);
      setActiveIndex(0);
      return;
    }
    if (checklist.length === 0) {
      return;
    }

    const showCard = window.setTimeout(() => {
      setChecklistVisible(true);
    }, CHECKLIST_ENTER_MS);

    const activateFirst = window.setTimeout(() => {
      setActiveIndex(0);
    }, FIRST_ROW_ACTIVE_MS);

    return () => {
      window.clearTimeout(showCard);
      window.clearTimeout(activateFirst);
    };
  }, [checklist.length, freezeAutoComplete]);

  useEffect(() => {
    if (freezeAutoComplete || checklist.length === 0 || activeIndex < 0) {
      return;
    }
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => Math.min(prev + 1, checklist.length - 1));
    }, ROW_ADVANCE_MS);
    return () => window.clearInterval(timer);
  }, [activeIndex, checklist.length, freezeAutoComplete]);

  const rows = checklist.length > 0 ? checklist : [...LOADER_COPY.rows];

  return (
    <QuizStepLayout
      testId="onboarding-loader-step"
      header={
        <QuizProgressHeader
          progressIndex={0}
          progressTotal={0}
          showProgress={false}
          variant="dark"
        />
      }
    >
      <div
        className="relative flex flex-1 flex-col items-center px-6 pt-8 md:px-0"
        data-figma-node="96121:8444"
        data-figma-node-pc="96344:110581"
      >
        <LoaderEdHero className="relative z-[1] mt-8" />

        <h2
          className="headline_regular_m relative z-[1] mt-6 text-content_inverse_primary md:headline_regular_m"
          data-testid="loader-title"
        >
          {LOADER_COPY.title}
          <motion.span
            className="inline-flex gap-0.5 pl-1"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          >
            ...
          </motion.span>
        </h2>

        <motion.div
          className="relative z-[1] mt-10 w-full max-w-[300px] rounded-xl bg-content_secondary p-5"
          data-testid="loader-checklist"
          data-figma-node="96105:47702"
          initial={{ opacity: 0, y: 12 }}
          animate={
            checklistVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }
          }
          transition={{
            duration: 0.4,
            ease: [0.2, 0, 0, 1],
          }}
        >
          {rows.map((label, index) => {
            const status =
              activeIndex < 0
                ? "pending"
                : index < activeIndex
                  ? "done"
                  : index === activeIndex
                    ? "active"
                    : "pending";
            return <LoaderCheckRow key={label} label={label} status={status} />;
          })}
        </motion.div>
      </div>
    </QuizStepLayout>
  );
};
